import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import Dialog from '@mui/material/Dialog';

import { DialogTitle, DialogContent } from '../../dialogs';
import SimpleTable from '../../SimpleTable';
import { failOnHttpError } from '../../../lib/utils'
import { loadTableData } from '../../table-utils';
import { Form, FormCheckbox, FormTextField, useFormikContext } from '../../Formik/forms';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CheckIcon from '@mui/icons-material/Check'
import { useParams } from "react-router-dom"
import MainContainer from '../../../components/layout/MainContainer/MainContainer'
import { useNotifications } from "lib/NotificationsProvider";
import { useOrbit } from "providers/OrbitProvider";
import { AuditLogRecord, getRecordIdentity, OrganizationRecord, store, UserRecord } from "lib/DataModel";
import { Roles } from "./Roles";
import { InitializedRecord } from '@orbit/records'
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { Checkbox, FormControlLabel, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import Dropdown from "components/UI/Dropdown";
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { useCurrentUser } from "lib/CurrentUserProvider";
import { userIsAdmin } from "lib/auth-helpers";
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import StandardDialog from "components/UI/Dialog/StandardDialog";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import SyncIcon from '@mui/icons-material/Sync'
import { useMutation, useSuspenseQuery } from "@apollo/client";
import { AuthToken, UpdateOrganizationInput } from "generated/graphql";
import { DataManager } from "components/SimpleTable/DataManager";
import { useHostname } from "lib/HostnameProvider";
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import Moment from "react-moment";
import useClipboard from "hooks/useClipboard";
import { gql } from "generated/gql";
import { Column } from "components/SimpleTable/types";
import { useTimezone } from "lib/TimezoneProvider";


type AuditLogDataRow = AuditLogRecord['attributes']
type UserDataRow = UserRecord['attributes'] & { id: string }

const COLUMNS: Column<UserDataRow>[] = [
  { title: 'ID', field: 'id', type: 'numeric', width: '0px', visible: true },
  { title: 'Name', field: 'name', visible: true },
  { title: 'Email', field: 'email', visible: true },
  { title: 'Roles', render: (_value, row) => <Roles user={row}/>, visible: true },
  { title: 'Organization Admin?', field: 'permitOrganizationAdmin', render: (permitOrganizationAdmin: boolean) => permitOrganizationAdmin ? <CheckIcon/> : null, visible: true },
  { title: 'SCIM Managed?', field: 'scimManaged', render: (scimManaged: boolean) => scimManaged ? <CheckIcon/> : null, visible: false },
]

const noneOption = { id: '(none)', attributes: {name: '(none)'}}

const findMatchingRole = (roleOptions, selectedClientRoleIds) => {
  const selectedMatch = roleOptions.find(role =>
    selectedClientRoleIds.includes(role.id)
  )

  return selectedMatch || noneOption
}

function ClientRoleDropdown({ client, selectedClientRoleIds, setSelectedClientRoleIds }) {
  const { setFieldValue } = useFormikContext()

  const clientRoles = store.cache.query(q => q.findRelatedRecords(client, 'clientRoles')) as InitializedRecord[]
  const roleOptions = [noneOption, ...clientRoles]

  const selectedRole =  findMatchingRole(roleOptions, selectedClientRoleIds);

  // remove this client's client role ids and set to the item chosen
  const handleSelect = (selected) => {
    setSelectedClientRoleIds(prevIds => {
      const filteredIds = prevIds?.filter(id => !clientRoles.map(roles => roles.id).includes(id)) || []
      const updatedClientRoleIds = [
        ...filteredIds,
        selected.id
      ].filter(id => id !== noneOption.id)

      setFieldValue('clientRoleIds', updatedClientRoleIds)
      return updatedClientRoleIds
    })
  }

  const renderItem = useCallback((itemOption) => {
    return itemOption.attributes.name
  }, [])

  return (
    <Dropdown
      key={client.id}
      className='clientRoleSelector'
      label='Role'
      collection={roleOptions}
      item={selectedRole}
      setItem={handleSelect}
      renderItem={renderItem}
      selectFirstByDefault={false}
      sx={{ maxWidth: 'unset' }}
    />
  )
}

function ClientRoles({ user, organization }) {
  const userClientRoles = user.id ? store.cache.query(q => q.findRelatedRecords(user, 'clientRoles')) as InitializedRecord[] : []
  const [selectedClientRoleIds, setSelectedClientRoleIds] = useState(userClientRoles.map(role => role.id))
  const clients = store.cache.query(q => q.findRelatedRecords(organization, 'clients')) as InitializedRecord[]

  if(clients.length > 0) {
    return (
      <>
        <Typography variant="h2">Client Roles</Typography>
        { clients?.map(client => (
          <Stack key={client.id} direction='row' gap={4} sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', mt: 2 }}>
            <Box sx={{ width: '50%' }}>{`${client.attributes?.name}`}</Box>
            <Box sx={{ width: '50%' }}><ClientRoleDropdown client={client} selectedClientRoleIds={selectedClientRoleIds} setSelectedClientRoleIds={setSelectedClientRoleIds} /></Box>
          </Stack>
          ))
        }
      </>
    )
  } else {
    return null
  }
}

type EditOrgModalProps = {
  organization: OrganizationRecord
  setOrganization: React.Dispatch<React.SetStateAction<OrganizationRecord>>
  onClose: () => void
}

const EditOrgModal = function({ organization, setOrganization, onClose }: EditOrgModalProps) {
  return (
    <Dialog aria-labelledby="modal-title" open={true} onClose={onClose} sx={{ '.MuiDialog-paper': {width: '600px'} }}>
      <DialogTitle id="modal-title" onClose={onClose}>
        Edit organization
      </DialogTitle>
      <DialogContent>
        <Form
          resource={organization}
          setResource={setOrganization}
          onSave={onClose}
          redirectAfterSave={false}
          promptOnNavigate={false}
          backwardsCompatibleMargins={true}
        >
          <FormTextField name="sessionTimeoutMinutes" label="Session Timeout" placeholder="Timeout in minutes (leave blank for no timeout)" sx={{ pr: 7 }} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const AddUserModal = function(props) {
  const [user, setUser] = useState(props.user);

  return (
    <Dialog aria-labelledby="modal-title" open={true} onClose={props.onClose}>
      <DialogTitle id="modal-title" onClose={props.onClose}>
        {user.id ? 'Edit' : 'Add'} user
      </DialogTitle>
      <DialogContent>
        <Form
          resource={user}
          setResource={setUser}
          relatedResource={props.organization}
          onSave={props.onSave}
          redirectAfterSave={false}
          promptOnNavigate={false}
          backwardsCompatibleMargins={true}
        >
          <FormTextField name="name" label="Name"/>
          <FormTextField name="email" label="Email" inputProps={{autoComplete: 'new-password'}}/>
          <FormTextField name="password" label="Password" type="password" inputProps={{autoComplete: 'new-password'}}/>
          <FormCheckbox name="permitOrganizationAdmin" label="Organization admin?"/>
          <ClientRoles user={user} organization={props.organization} />
        </Form>
      </DialogContent>
    </Dialog>
  );
};

const GET_ORGANIZATION_QUERY = gql(`
  query GetOrganization($id: ID!) {
    organization(id: $id) {
      ...OrganizationFields
    }
  }
`)

export const UPDATE_ORGANIZATION_MUTATION = gql(`
  mutation UpdateOrganization(
    $id: ID!
    $scimEnabled: Boolean
    $createAuthTokens: [AuthTokenInput!]
    $name: String
    $permitAiAnalysis: Boolean
    $permitPredictedValue: Boolean
  ) {
    mutationResult:updateOrganization (input: {
      id: $id
      name: $name
      permitAiAnalysis: $permitAiAnalysis
      permitPredictedValue: $permitPredictedValue
      scimEnabled: $scimEnabled
      createAuthTokens: $createAuthTokens
    }) {
      id
      errors
      organization {
        ...OrganizationFields
      }
    }
  }
`)

type ProvisionModalProps = {
  organizationId: string
  organizationName: string
  onClose: () => void
}

const ProvisionModal = function({ organizationId, organizationName, onClose }: ProvisionModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const { addNotification } = useNotifications()
  const { data: { organization } } = useSuspenseQuery(GET_ORGANIZATION_QUERY, { variables: { id: organizationId }})
  const { apiHostname } = useHostname()
  const scimUrl = `https://${apiHostname}/scim`

  const [authToken, setAuthToken] = useState<AuthToken | null>(() => organization.authTokens.find(item => item.scope.includes('scim')) ?? null)
  const [scimEnabled, setScimEnabled] = useState(organization.scimEnabled)

  const displayToken = authToken?.token ?? (authToken?.tokenPrefix ? `${authToken.tokenPrefix}••••••••••••` : '')
  const hasChanged = organization.scimEnabled !== scimEnabled
  const hasUnmaskedToken = !!authToken?.token

  const [gqlUpdate] = useMutation(UPDATE_ORGANIZATION_MUTATION)
  const refreshToken = useCallback(() => {
    setSubmitting(true)

    const mutation = {
      id:               organization.id,
      scimEnabled:      true,
      createAuthTokens: [
        {
          scope:         ['scim'],
          destroyOthers: true,
        },
      ],
    }
    gqlUpdate({ variables: mutation }).then((result) => {
      if(scimEnabled) {
        const newAuthToken = result.data?.mutationResult?.organization?.authTokens?.find(item => item.scope.includes('scim') && item.token)
        newAuthToken && setAuthToken(newAuthToken)
      }
    }).catch(_error => {
      addNotification({variant: 'alert', message: "Trouble refreshing token. Please try again later."})
    }).finally(() => {
      setSubmitting(false)
    })
  }, [addNotification, gqlUpdate, organization.id, scimEnabled])

  const handleSave = useCallback(() => {
    setSubmitting(true)

    const mutation: UpdateOrganizationInput = {
      id:               organization.id as string,
      scimEnabled:      scimEnabled,
    }

    if (scimEnabled === true && !authToken) {
      mutation.createAuthTokens = [
        {
          scope:         ['scim'],
          destroyOthers: true,
        },
      ]
    }

    gqlUpdate({ variables: mutation }).then((result) => {
      if(scimEnabled) {
        const newAuthToken = result?.data?.mutationResult?.organization?.authTokens?.find(item => item.scope.includes('scim') && item.token)
        newAuthToken && setAuthToken(newAuthToken)
      }
    }).catch(_error => {
      addNotification({variant: 'alert', message: "Trouble updating provisioning. Please try again later."})
    }).finally(() => {
      setSubmitting(false)
    })
  }, [addNotification, gqlUpdate, organization.id, scimEnabled, authToken])

  const handleCheckboxClick = useCallback((event) => {
    const checked = event.target.checked

    setScimEnabled(checked)
  }, [])

  const { copyToClipboard } = useClipboard()

  return (
    <StandardDialog
      title={`Provisioning for ${organizationName}`}
      onCancel={onClose}
      onConfirm={handleSave}
      disabled={submitting || !hasChanged}
      confirmText='Save'
      maxWidth='600px'
    >
      <Stack direction='column' sx={{ mr: 7 }} gap={2}>
        <FormControlLabel control={<Checkbox checked={!!scimEnabled} onClick={handleCheckboxClick}/>} label="Enable SCIM" />
        <>
        </>
        <Stack direction='row' sx={{ mt: 2, justifyContent:'space-between', alignItems:'center' }}>
          <Typography variant="h4" sx={{ }}>
            SCIM Endpoint:
          </Typography>
          <TextField
            value={scimEnabled && authToken ? scimUrl : ''}
            placeholder="Enable SCIM and Save to populate"
            variant="outlined"
            disabled
            InputProps={{
              endAdornment: (
                scimEnabled && authToken &&
                <InputAdornment position="end" sx={{ color: 'black' }}>
                  <IconButton aria-label="Copy URL" onClick={() => copyToClipboard(scimUrl)}  sx={{ width: '30px', height: '30px' }}>
                    <ContentCopyRoundedIcon sx={{  }}/>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width:350
            }}
          />
        </Stack>
        <Stack direction='row' sx={{ justifyContent:'space-between', alignItems:'center' }}>
          <Typography variant="h4" sx={{ }}>
            Token:
          </Typography>

          <TextField
            value={scimEnabled ? displayToken : ''}
            variant="outlined"
            disabled
            InputProps={{
              endAdornment: (
                scimEnabled && displayToken &&
                <>
                  <InputAdornment position="end" sx={{  }}>
                    <IconButton aria-label="Refresh Token" onClick={refreshToken} sx={{ width: '30px', height: '30px' }}>
                      <SyncIcon style={{ transform: 'rotate(45deg)' }} />
                    </IconButton>
                  </InputAdornment>
                  <InputAdornment position="end" sx={{ display: hasUnmaskedToken ? undefined : 'none !important' }}>
                    <IconButton aria-label="Copy URL" sx={{ width: '30px', height: '30px' }} onClick={() => copyToClipboard(displayToken)}>
                      <ContentCopyRoundedIcon sx={{  }}/>
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
            sx={{
              width:350
            }}
          />
        </Stack>
      </Stack>
    </StandardDialog>
  );
};


const AUDIT_LOG_COLUMNS: Column<AuditLogDataRow>[] = [
  { title: 'Description', field: 'description', visible: true, sortable: false } as const,
  { title: 'Timestamp', field: 'createdAt', render: (value: string) => <Moment local format="LLLL">{value}</Moment>, align: 'right', visible: true, sortable: false } as const,
]

type AuditLogModalProps = {
  organization: OrganizationRecord
  onClose: () => void
}

const AuditLogModal = function({ organization, onClose }: AuditLogModalProps) {
  const { remote } = useOrbit()
  const { addNotification } = useNotifications()
  const dataManagerRef = useRef<DataManager<AuditLogDataRow>>(null);
  const { timezone } = useTimezone()

  const dataFn = useMemo(() => {
    if(organization) {
      return loadTableData(remote, q => q.findRelatedRecords(organization, 'auditLogEvents'), addNotification, timezone)
    } else {
      return []
    }
  }, [addNotification, organization, remote, timezone])


  return (
    <StandardDialog
      title='Audit Log'
      maxWidth='800px'
      onCancel={onClose}
      cancelText='Close'
      sx={{
        '.MuiDialogContent-root > .MuiBox-root': {
          py: 0,
          minHeight: '515px',
        }
      }}
    >
        <SimpleTable
          dataManagerRef={dataManagerRef}
          columns={AUDIT_LOG_COLUMNS}
          data={dataFn}
          options={{
            paginated: true,
            pageSize: 10,
          }}
          actions={[]}
          hasTabNavigation
        />
    </StandardDialog>
  );
};

export default function UserTable() {
  const { organizationId } = useParams() as { organizationId: string }
  const { store, remote } = useOrbit()
  const { addNotification } = useNotifications()
  const [userForModal, setUserForModal] = useState<null | UserRecord>()
  const [organization, setOrganization] = useState<OrganizationRecord>();
  const [first, setFirst] = useState(true);
  const dataManagerRef = useRef<DataManager<UserDataRow>>(null);
  const { timezone } = useTimezone()

  const { currentUser } = useCurrentUser()
  const [orgModalOpen, setOrgModalOpen] = useState(false)
  const [provisionModalOpen, setProvisionModalOpen] = useState(false)
  const [auditModalOpen, setAuditModalOpen] = useState(false)

  useEffect(() => {
    remote.query(q =>
      q.findRecord({ type: "organization", id: organizationId }), {
        sources: {
          remote: {
            include: ['clients', 'clients.client_roles'],
            fields: { 'client': ['name', 'client_roles'], 'client_role': ['name'] }
          }
        }
      })
      .then((organization: OrganizationRecord) => {
        setOrganization(organization)
      })
      .catch(_error => {
        addNotification({variant: 'alert', message: 'Failed to load organization.'});
      })
  }, [addNotification, organizationId, remote, store.cache]);

  const refreshTable = () => {
    dataManagerRef.current && dataManagerRef.current.reload();
  };

  useEffect(() => {
    if(first) {
      setFirst(false)
    } else {
      refreshTable()
    }
  }, [first, organizationId])

  const handleDelete = (_event: unknown, rowData: UserDataRow) => {
    if(!confirm(`Are you sure you want to delete the user ${rowData.email}?`)) {
      return;
    }

    fetch(`/api/v1/organizations/${organizationId}/users/${rowData.id}`, { method: 'DELETE' })
      .then(failOnHttpError)
      .then(() => {
        addNotification({variant: 'notice', message: 'Deleted user.'});
        refreshTable();
      })
      .catch(_error => {
        addNotification({variant: 'alert', message: 'Failed to delete user.'});
      });
  };

  const handleEdit = (_event: unknown, rowData: UserDataRow) => {
    const user = store.cache.query<UserRecord>(q => q.findRecord(getRecordIdentity('user', rowData.id)))
    setUserForModal(user);
  };

  const handleAdd = (_event: unknown, _rowData: UserDataRow) => {
    setUserForModal({type: 'user', id: '', attributes: {name: '', email: '', password: '', permitOrganizationAdmin: false}});
  };

  const closeModal = () => {
    setUserForModal(null);
  };

  const afterAddUser = (_user: UserRecord) => {
    closeModal();
    refreshTable();
  };

  const dataFn = useMemo(() => {
    if(organization) {
      return loadTableData(remote, q => q.findRelatedRecords(organization, 'users'), addNotification, timezone)
    } else {
      return []
    }
  }, [addNotification, organization, remote, timezone])

  return (
    <MainContainer>
      <div style={{ maxWidth: "100%" }}>
        <SimpleTable
          dataManagerRef={dataManagerRef}
          columns={COLUMNS}
          data={dataFn}
          options={{
            paginated: true,
            pageSize: 10,
            columnsButton: true,
          }}
          title={`Users for ${organization && organization.attributes.name || ''}`}
          rowActions={[
            {
              icon: () => <DeleteOutlineIcon/>,
              tooltip: 'Delete User',
              onClick: handleDelete,
            },
            {
              icon: () => <EditIcon/>,
              tooltip: 'Edit user',
              onClick: handleEdit,
            },
          ]}
          actions={[
            {
              icon: () => <AddCircleIcon/>,
              isFreeAction: true,
              tooltip: 'Add new user',
              onClick: handleAdd,
            },
            userIsAdmin({currentUser}) && {
              icon: () => <CorporateFareIcon/>,
              isFreeAction: true,
              tooltip: 'Organization settings',
              onClick: () => setOrgModalOpen(true),
            },
            userIsAdmin({currentUser}) && {
              icon: () => <GroupAddOutlinedIcon/>,
              isFreeAction: true,
              tooltip: 'Provisioning',
              onClick: () => setProvisionModalOpen(true),
            },
            userIsAdmin({currentUser}) && {
              icon: () => <RecentActorsOutlinedIcon/>,
              isFreeAction: true,
              tooltip: 'Audit log',
              onClick: () => setAuditModalOpen(true),
            },
          ].filter(Boolean)}
        />
      </div>
      {userForModal && (
        <AddUserModal
          user={userForModal}
          organization={organization}
          onClose={closeModal}
          onSave={afterAddUser}
        />
      )}
      {organization && orgModalOpen && (
        <EditOrgModal
          organization={organization}
          setOrganization={setOrganization}
          onClose={() => setOrgModalOpen(false)}
        />
      )}
      {organization && provisionModalOpen && (
        <ProvisionModal
          organizationId={organizationId}
          organizationName={organization?.attributes.name ?? ''}
          onClose={() => setProvisionModalOpen(false)}
        />
      )}
      {organization && auditModalOpen && (
        <AuditLogModal
          organization={organization}
          onClose={() => setAuditModalOpen(false)}
        />
      )}
    </MainContainer>
  )
}
